import React from "react";
import { Alert, Box, Button, CircularProgress, styled } from "@mui/material";
import { IContent } from "@vmosolutions/oc-interfaces";
import { useDispatch } from "react-redux";
import { openWindow } from "src/redux/actions/Mosaic.Actions";

/** Shared error copy for every content preview viewer - keeps the wording in sync. */
export const FILE_PREVIEW_ERROR_MESSAGE =
  "There was an error loading this file. Please try again, or contact the administrator.";

const ShellBox = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  height: "calc(100svh - 160px)",
  "& .viewerBody": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .viewerLoading": {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none",
  },
}));

interface IViewerShellProps {
  file: IContent;
  loading: boolean;
  error: boolean;
  /** Hide the footer "Edit Content" button (e.g. when the viewer renders its own controls). */
  hideEditButton?: boolean;
  children: React.ReactNode;
}

/**
 * Common chrome for the content preview viewers: the shared error alert, a centred loading
 * overlay, and the "Edit Content" footer button. The body is always mounted (the spinner is an
 * overlay, not a replacement) so children like <img>/<iframe> can load underneath it.
 */
export function ViewerShell({ file, loading, error, hideEditButton, children }: IViewerShellProps): JSX.Element {
  const dispatch = useDispatch();

  if (error) {
    return (
      <Alert severity="error" variant="filled">
        {FILE_PREVIEW_ERROR_MESSAGE}
      </Alert>
    );
  }

  return (
    <ShellBox>
      <Box className="viewerBody">{children}</Box>
      {loading && (
        <Box className="viewerLoading">
          <CircularProgress />
        </Box>
      )}
      {!hideEditButton && (
        <Box sx={{ display: "flex", justifyContent: "start", p: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => {
              dispatch(openWindow("Edit Content", "ManageContentWindow", [], { contentId: file._id }, "context"));
            }}
          >
            Edit Content
          </Button>
        </Box>
      )}
    </ShellBox>
  );
}
